import type { CirclesFlashSpec, CirclesFloatingText } from './circlesRenderTypes';
import { hexToRgba } from './circlesRenderColour';
import type { RenderQuality } from '../../rendering/renderQuality';

/**
 * Screen-edge flash overlays. Geometry per type:
 *
 *   - `drop` / `death`: a full-screen wash. `death` uses the same
 *     visceral red as the rest of the failure vocabulary; `drop` is
 *     pure white reserved for high-impact celebratory triggers.
 *   - `hp_loss`: a radial vignette that pools red at the edges and
 *     fades to transparent in the centre. Reads as "the playfield
 *     is bleeding" without obscuring the action — distinct from
 *     `death` (which would obscure) and from the thin verdict bars
 *     (which lose their weight when the player is mid-stream).
 *   - `milestone`: a 4-edge golden frame, sized by intensity.
 *   - everything else (perfect/good/ok/miss): a thin pair of edge
 *     bars at the top + bottom. Subtle so they don't dominate the
 *     screen on every hit.
 *
 * `intensity` modulates both alpha and thickness. `f.t` (remaining
 * life) ramps everything down so the flash never persists past its
 * configured life. Mutates the `flashes` array in place — drops
 * expired entries during the same pass.
 */
export function drawFlashes(
  ctx: CanvasRenderingContext2D,
  flashes: CirclesFlashSpec[],
  w: number,
  h: number,
  dt: number,
): void {
  for (let i = flashes.length - 1; i >= 0; i--) {
    const f = flashes[i];
    f.t -= dt;
    if (f.t <= 0) {
      flashes.splice(i, 1);
      continue;
    }
    const intensity = f.intensity ?? 1;
    const a = f.t * 1.5 * intensity;
    ctx.save();
    ctx.globalAlpha = Math.min(1, a);
    const colour =
      f.type === 'milestone'
        ? f.colour ?? '#ffd84d'
        : f.type === 'perfect'
          ? '#3bff9d'
          : f.type === 'good'
            ? '#16e0ff'
            : f.type === 'ok'
              ? '#ffd84d'
              : f.type === 'miss'
                ? '#ff3b5c'
                : f.type === 'drop'
                  ? '#ffffff'
                  : '#ff3b5c';
    if (f.type === 'drop' || f.type === 'death') {
      ctx.fillStyle = colour;
      ctx.fillRect(0, 0, w, h);
    } else if (f.type === 'hp_loss') {
      // Radial vignette: red at the edges, fading toward transparent
      // in the centre. Uses inner radius ~30% of the shorter axis
      // so the centre stays clean enough for the player to keep
      // tracking circles; outer radius covers the full screen
      // diagonal so all four corners receive paint.
      const cx = w / 2;
      const cy = h / 2;
      const innerR = Math.min(w, h) * 0.3;
      const outerR = Math.hypot(w / 2, h / 2);
      const grad = ctx.createRadialGradient(cx, cy, innerR, cx, cy, outerR);
      grad.addColorStop(0, 'rgba(255, 59, 92, 0)');
      grad.addColorStop(0.55, hexToRgba(colour, 0.35 * intensity));
      grad.addColorStop(1, hexToRgba(colour, 0.75 * intensity));
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
    } else if (f.type === 'milestone') {
      ctx.fillStyle = colour;
      const thickness = Math.min(28 * intensity, f.t * 90);
      ctx.fillRect(0, 0, w, thickness);
      ctx.fillRect(0, h - thickness, w, thickness);
      ctx.fillRect(0, 0, thickness, h);
      ctx.fillRect(w - thickness, 0, thickness, h);
    } else {
      ctx.fillStyle = colour;
      const thickness = Math.min(10 * intensity, f.t * 60);
      ctx.fillRect(0, 0, w, thickness);
      ctx.fillRect(0, h - thickness, w, thickness);
    }
    ctx.restore();
  }
}

/**
 * Render judgement / damage floaters with a dark stroke outline so
 * they stay readable when they sit on top of busy areas (slider
 * bodies, kiai bloom, dense hit-burst clouds) and inside
 * same-coloured FX (e.g. red "Miss" on a red miss shockwave).
 *
 * `big` floaters get an extra peak-of-life scale-pop (1.0 → 1.18
 * over the first 80ms) so milestone labels feel "punched" into
 * the playfield instead of just appearing. Reduced-motion users
 * skip the pop — the outline + colour are enough to communicate.
 *
 * Mutates `texts` in place — drops expired entries during the same
 * pass.
 */
export function drawFloatingTexts(
  ctx: CanvasRenderingContext2D,
  texts: CirclesFloatingText[],
  dt: number,
  reducedMotion: boolean,
  quality: RenderQuality,
): void {
  for (let i = texts.length - 1; i >= 0; i--) {
    const t = texts[i];
    t.life -= dt;
    t.y += t.vy * dt;
    t.vy *= 0.97;
    if (t.life <= 0) {
      texts.splice(i, 1);
      continue;
    }
    const progress = 1 - t.life / t.maxLife;
    const alpha = Math.min(1, t.life / 0.25);
    // Punch-in: text spawns at 1.18× and decays back to 1.0× over
    // the first ~25% of life. Keeps glyphs LEGIBLE from frame 1
    // (never smaller than baseline) while giving the moment a
    // "kicked" feel. Big floaters get the existing wobble layered
    // on top so milestone labels keep their extra weight.
    // Reduced-motion holds at 1.0× for a static appearance.
    const punchPhase = Math.min(1, progress / 0.25);
    const punchEase = reducedMotion ? 0 : 1 - punchPhase * punchPhase;
    const punchScale = 1 + punchEase * 0.18;
    const wobble = t.big && !reducedMotion ? 1 + Math.sin(progress * 4) * 0.05 : 1;
    const scale = t.scale * punchScale * wobble;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(t.x, t.y);
    ctx.scale(scale, scale);
    ctx.font = `bold ${t.big ? 44 : 28}px 'Rajdhani', 'Orbitron', system-ui, sans-serif`;
    ctx.textAlign = 'center';
    // Middle baseline so `y` is the visual centre of the glyph.
    // Session-side `spawnFloatingText` offsets by radius + half
    // font-height + gap, so middle-baseline here keeps the floater
    // bottom sitting cleanly above the hit circle's outer ring.
    ctx.textBaseline = 'middle';
    // Dark outline first — drawn with no shadow blur so the stroke
    // stays crisp against any background. `lineJoin = round` so
    // tight serifs on big letters don't spike into ugly miters at
    // peak scale.
    ctx.lineJoin = 'round';
    ctx.lineWidth = t.big ? 6 : 4;
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.85)';
    ctx.strokeText(t.text, 0, 0);
    // Coloured glow on the fill pass so the text reads as "lit"
    // from the verdict colour without bleeding through the outline.
    // Skipped on low-tier — the dark outline plus saturated fill
    // keeps the verdict legible.
    if (quality.chromeShadows) {
      ctx.shadowColor = t.colour;
      ctx.shadowBlur = t.big ? 24 : 12;
    }
    ctx.fillStyle = t.colour;
    ctx.fillText(t.text, 0, 0);
    ctx.restore();
  }
}

/**
 * Big centred 3-2-1-GO countdown overlay used during the lead-in.
 * Scale-pops and fades through each integer; `GO!` switches to the
 * perfect-green hue + glow as the final beat ticks over.
 */
export function drawCountdown(
  ctx: CanvasRenderingContext2D,
  countdown: number | null,
  w: number,
  h: number,
  quality: RenderQuality,
): void {
  if (countdown === null) {
    return;
  }
  const phase = countdown - Math.floor(countdown);
  const num = Math.ceil(countdown);
  const label = num > 0 ? String(num) : 'GO!';
  const scale = 1 + (1 - phase) * 0.6;
  ctx.save();
  ctx.globalAlpha = 0.4 + phase * 0.6;
  ctx.translate(w / 2, h / 2);
  ctx.scale(scale, scale);
  if (quality.chromeShadows) {
    ctx.shadowColor = label === 'GO!' ? '#3bff9d' : '#ff2bd6';
    ctx.shadowBlur = 40;
  }
  ctx.fillStyle = '#fff';
  ctx.font = `bold 96px 'Rajdhani', 'Orbitron', system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, 0, 0);
  ctx.restore();
}
